import React from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";

const tutorialSteps = [
  {
    id: 0,
    title: "CRP Basics",
    text: (
      <>
        To get started, click <b>CRP Basics</b> in the navigation bar.
      </>
    ),
    tabletText: (
      <>
        To get started, tap <b>CRP Basics</b> in the navigation bar.
      </>
    ),
    target: ".crp-basics",
    route: "/",
    advanceType: "click",
    canScroll: false,
  },
  {
    id: 1,
    title: "CRP Basics Page",
    text: (
      <>
        Here you will find basic information about CRP and answers to frequently
        asked questions.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Here you will find basic information about CRP and answers to frequently
        asked questions.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".crp-basics1",
    route: "/crpbasics",
    advanceType: "next",
    canScroll: true,
  },
  {
    id: 2,
    title: "Explore Practices",
    text: (
      <>
        To view CRP practices available in your area, click{" "}
        <b>Explore Practices</b>.
      </>
    ),
    tabletText: (
      <>
        To view CRP practices available in your area, tap{" "}
        <b>Explore Practices</b>.
      </>
    ),
    target: ".explore-practices",
    route: "/crpbasics",
    advanceType: "click",
    canScroll: false,
  },
  {
    id: 3,
    title: "State Selection",
    text: (
      <>
        Use the <b>State</b> dropdown to view currently available states.
        <br />
        <br />
        <i>Select one to continue.</i>
      </>
    ),
    target: ".state-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 4,
    title: "County Selection",
    text: (
      <>
        Once a state is selected, use the <b>County</b> dropdown to choose your
        county.
        <br />
        <br />
        <i>Make a selection to continue.</i>
      </>
    ),
    target: ".county-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 5,
    title: "Continue",
    text: (
      <>
        Wait for the map to finish zooming and highlight your county.
        <br />
        <br />
        Then click the <b>Next</b> button to continue.
      </>
    ),
    tabletText: (
      <>
        Wait for the map to finish zooming and highlight your county.
        <br />
        <br />
        Then tap the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "click",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 6,
    title: "Land Cover Type",
    text: (
      <>
        Click to select your <b>land cover type</b>. Hover over the
        <QuestionCircleOutlined style={{ marginLeft: 4 }} /> icon to see a
        description of each option.
        <br />
        <br />
        We’ll just pick one for now — but you can change or add more later.
      </>
    ),
    tabletText: (
      <>
        Tap to select your <b>land cover type</b>.
        <br />
        <br />
        We’ll just pick one for now — but you can change or add more later.
      </>
    ),
    target: ".land-cover-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 1,
  },
  {
    id: 7,
    title: "Continue",
    text: (
      <>
        Click the <b>Next</b> button to continue.
      </>
    ),
    tabletText: (
      <>
        Tap the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 1,
  },
  {
    id: 8,
    title: "Select Priorities",
    text: (
      <>
        Click to select a <b>priority</b> you’d like to address on your field.
        <br />
        <br />
        Hover over <QuestionCircleOutlined style={{ marginLeft: 4 }} /> to get a
        description of each priority.
        <br />
        <br />
        Select <b>three</b> for now. You can add or remove them later.
      </>
    ),
    tabletText: (
      <>
        Tap to select a <b>priority</b> you’d like to address on your field.
        <br />
        <br />
        Select <b>three</b> for now. You can add or remove them later.
      </>
    ),
    target: ".priority-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 2,
  },
  {
    id: 9,
    title: "Continue",
    text: (
      <>
        Click the <b>Next</b> button to continue.
      </>
    ),
    tabletText: (
      <>
        Tap the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 2,
  },
  {
    id: 10,
    title: "Rank Priorities",
    text: (
      <>
        Click and drag an icon to <b>reorder</b> your selected priorities.
        <br />
        <br />
        <i>Move one for now. You can change the order again later.</i>
      </>
    ),
    tabletText: (
      <>
        Tap and drag an icon to <b>reorder</b> your selected priorities.
        <br />
        <br />
        <i>Move one for now. You can change the order again later.</i>
      </>
    ),
    target: ".ranking-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 3,
  },
  {
    id: 11,
    title: "Confirm",
    text: (
      <>
        Click the <b>Confirm</b> button to continue.
      </>
    ),
    tabletText: (
      <>
        Tap the <b>Confirm</b> button to continue.
      </>
    ),
    target: ".confirm-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 3,
  },
  {
    id: 12,
    title: "Practice Card",
    text: (
      <>
        This is a <b>Practice Card</b>.
        <br />
        <br />
        It provides a quick summary of the practice and helps you compare
        options at a glance.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This is a <b>Practice Card</b>.
        <br />
        <br />
        It provides a quick summary of the practice and helps you compare
        options at a glance.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".practice-card-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 13,
    title: "Practice Number",
    text: (
      <>
        This is the <b>practice number</b>, often used when referring to
        practices.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This is the <b>practice number</b>, often used when referring to
        practices.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-number",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 14,
    title: "Practice Title",
    text: (
      <>
        This is the <b>full name</b> of the practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This is the <b>full name</b> of the practice.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-title",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 15,
    title: "Land Cover",
    text: (
      <>
        This shows the <b>land cover types</b> that can be enrolled in this
        practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This shows the <b>land cover types</b> that can be enrolled in this
        practice.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-cover",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 16,
    title: "Priorities",
    text: (
      <>
        These are the <b>conservation priorities</b> this practice helps
        address.
        <br />
        <br />
        <i>Hover over the icons to get a reminder of what each represents.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        These are the <b>conservation priorities</b> this practice helps
        address.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-priorities",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 17,
    title: "Signup Types",
    text: (
      <>
        These are the <b>signup types</b> available for this practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        These are the <b>signup types</b> available for this practice.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-signup",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 18,
    title: "Additional Incentives",
    text: (
      <>
        These are the <b>incentives</b> that may be available for this practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        These are the <b>incentives</b> that may be available for this practice.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-incentives",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 19,
    title: "Full Details",
    text: (
      <>
        You can click <b>Details</b> to open a new tab with a more comprehensive
        overview of the practice, including its requirements.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You can tap <b>Details</b> to open a new tab with a more comprehensive
        overview of the practice, including its requirements.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-details",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 20,
    title: "Priority Match",
    text: (
      <>
        The <b>priority match</b> shows how well this practice aligns with your
        selections.
        <br />
        <i>Rated from Best to Fair.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        The <b>priority match</b> shows how well this practice aligns with your
        selections.
        <br />
        <i>Rated from Best to Fair.</i>
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-match",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 21,
    title: "Most Common Flag",
    text: (
      <>
        This practice is marked with the green <b>Most Common</b> flag.
        <br />
        <br />
        These show practices which are frequently enrolled by landowners in your
        selected area.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This practice is marked with the green <b>Most Common</b> flag.
        <br />
        <br />
        These show practices which are frequently enrolled by landowners in your
        selected area.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-most-common-card",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
    condition: "hasMostCommon",
  },

  {
    id: 22,
    title: "CREP Practices",
    text: (
      <>
        This practice has additional options under the <b>CREP</b> program.
        <br />
        <br />
        Click the card to flip and view the available CREP options.
        <br />
        <br />
        <b>Click the Card to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This practice has additional options under the <b>CREP</b> program.
        <br />
        <br />
        Tap the card to flip and view the available CREP options.
        <br />
        <br />
        <b>Tap the Card to continue.</b>
      </>
    ),
    target: ".tutorial-crep-card",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    condition: "hasCrep",
  },

  {
    id: 23,
    title: "Practice Details & CREP Options",
    text: (
      <>
        Here you'll find links to the <b>CREP details</b> page and the general
        practice details.
        <br />
        <br />
        Clicking one of these links will open a new page with more information.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        Here you'll find links to the <b>CREP details</b> page and the general
        practice details.
        <br />
        <br />
        Tapping one of these links will open a new page with more information.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".tutorial-crep-back-content",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
    condition: "hasCrep",
  },
  {
    id: 24,
    title: "Viewing Options",
    text: (
      <>
        You can use this dropdown to switch between <b>Grid View</b> and{" "}
        <b>List View</b>.
        <br />
        <br />
        It also includes an option to <b>print</b> your filtered practices.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You can use this dropdown to switch between <b>Grid View</b> and{" "}
        <b>List View</b>.
        <br />
        <br />
        It also includes an option to <b>print</b> your filtered practices.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".list-view-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 25,
    title: "Change Selections",
    text: (
      <>
        You can click the <b>Filters</b> button to reopen the filter panel.
        <br />
        <br />
        From there you can update your selected priorities or adjust their
        rankings here.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You can tap the <b>Filters</b> button to reopen the filter panel.
        <br />
        <br />
        From there you can update your selected priorities or adjust their
        rankings here.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".filter-button-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 26,
    title: "Reset All",
    text: (
      <>
        You can click <b>Start Over</b> to start your search over at any time.
        <br />
        <br />
        <i>This will remove all your previous selections and filters.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You can tap <b>Start Over</b> to start your search over at any time.
        <br />
        <br />
        <i>This will remove all your previous selections and filters.</i>
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".reset-button-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 27,
    title: "Check Field Eligibility",
    text: (
      <>
        Click <b>Check Field Eligibility</b> to see which practices are
        available for your speciic field and view estimated payments.
      </>
    ),
    tabletText: (
      <>
        Tap <b>Check Field Eligibility</b> to see which practices are available
        for your speciic field and view estimated payments.
      </>
    ),
    target: ".tutorial-ce-button",
    route: "/PracticeList",
    advanceType: "click",
    canScroll: false,
  },

  {
    id: 28,
    title: "Field Options",
    text: (
      <>
        Here are the tools to find or define your field — by drawing it,
        uploading a file, or selecting from existing fields.
        <br />
        <br />
        Your field helps us calculate eligibility, soil types, and rental rates.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        Here are the tools to find or define your field — by drawing it,
        uploading a file, or selecting from existing fields.
        <br />
        <br />
        Your field helps us calculate eligibility, soil types, and rental rates.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".sidebar-btn-container",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 29,
    title: "Drawing Your Field",
    text: (
      <>
        This option lets you outline your field directly on the map using what's
        called a polygon tool.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This option lets you outline your field directly on the map using what's
        called a polygon tool.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".polygon-btn",
    route: "/Map",
    advanceType: "next",
    canScroll: true,
  },

  {
    id: 30,
    title: "Drop a Pin",
    text: (
      <>
        This option allows you to drop a pin on the map.
        <br />
        We'll automatically find the nearest field boundary where the pin lands.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This option allows you to drop a pin on the map.
        <br />
        We'll automatically find the nearest field boundary where the pin lands.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".pin-btn",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 31,
    title: "Pick from Fields",
    text: (
      <>
        You can use this to browse and select from existing field boundaries on
        the map.
        <br />
        It offers a more visual way to choose your field.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You can use this to browse and select from existing field boundaries on
        the map.
        <br />
        It offers a more visual way to choose your field.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".boundary-btn",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 32,
    title: "Upload A Shapefile",
    text: (
      <>
        Already have a shapefile? Upload it as a <code>.zip</code> that
        contains:
        <ul style={{ fontSize: "14px" }}>
          <li style={{ lineHeight: "18px" }}>
            <code>.shp</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.shx</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.dbf</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.prj</code>
          </li>
        </ul>
        Not sure what a shapefile is? You can skip this.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        Already have a shapefile? Upload it as a <code>.zip</code> that
        contains:
        <ul style={{ fontSize: "14px" }}>
          <li style={{ lineHeight: "18px" }}>
            <code>.shp</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.shx</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.dbf</code>
          </li>
          <li style={{ lineHeight: "18px" }}>
            <code>.prj</code>
          </li>
        </ul>
        Not sure what a shapefile is? You can skip this.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".shp-button",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 33,
    title: "The Map",
    text: (
      <>
        You might have noticed movement over here when this page loaded.
        <br />
        <br />
        We automatically zoom to your previously selected county.
        <br />
        <br />
        Now you're ready to define your field using one of the options we just
        introduced.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        You might have noticed movement over here when this page loaded.
        <br />
        <br />
        We automatically zoom to your previously selected county.
        <br />
        <br />
        Now you're ready to define your field using one of the options we just
        introduced.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 34,
    title: "Base Layers",
    text: (
      <>
        This button lets you choose between:
        <ul>
          <li>A satellite base map</li>
          <li>A dark-themed street map</li>
          <li>A light-themed street map</li>
        </ul>
        You can also turn on helpful layers like SAFE or CREP boundaries.
        <br />
        <br />
        We're currently using the satellite base map to make it easier to
        identify a field.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This button lets you choose between:
        <ul>
          <li>A satellite base map</li>
          <li>A dark-themed street map</li>
          <li>A light-themed street map</li>
        </ul>
        You can also turn on helpful layers like SAFE or CREP boundaries.
        <br />
        <br />
        We're currently using the satellite base map to make it easier to
        identify a field.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".layer-select",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 35,
    title: "Let's Draw a Field",
    text: (
      <>
        Let’s get started by drawing a polygon to represent the area you want to
        assess.
        <br />
        <br />
        <b>Click Draw Your Field Boundary to continue.</b>
      </>
    ),
    tabletText: (
      <>
        Let’s get started by drawing a polygon to represent the area you want to
        assess.
        <br />
        <br />
        <b>Tap Draw Your Field Boundary to continue.</b>
      </>
    ),
    target: ".polygon-btn",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },
  {
    id: 36,
    title: "Acknowledgement",
    text: (
      <>
        This message confirms that you understand this tool is for informational
        purposes only and does not guarantee eligibility.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        This message confirms that you understand this tool is for informational
        purposes only and does not guarantee eligibility.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".nongeocheck-form",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 37,
    title: "Non-Spatial Requirements",
    text: (
      <>
        These are eligibility requirements that are not tied to your field's
        location, such as commodity status, owner's AGI, and length of
        ownership.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        These are eligibility requirements that are not tied to your field's
        location, such as commodity status, owner's AGI, and length of
        ownership.
        <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".ant-alert-with-description",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 38,
    title: "Acknowledgement",
    text: (
      <>
        Click <b>Acknowledge</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Tap <b>Acknowledge</b> to continue.
      </>
    ),
    target:
      ".ant-modal-content .ant-btn.ant-btn-primary.ant-btn-color-primary.ant-btn-variant-solid",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },
  {
    id: 39,
    title: "Previous Selections",
    text: (
      <>
        Here you'll be reminded of the selections you previously made. <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    tabletText: (
      <>
        Here you'll be reminded of the selections you previously made. <br />
        <br />
        <b>Tap Next to continue.</b>
      </>
    ),
    target: ".previous-selections",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 40,
    title: "Edit or Confirm",
    text: (
      <>
        You'll be able to decide whether you want to confirm and continue with
        the current selections, or edit you selections.
        <br />
        <br />
        For now we'll confirm and continue.
        <br />
        <br />
        Click <b>Confirm</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You'll be able to decide whether you want to confirm and continue with
        the current selections, or edit you selections.
        <br />
        <br />
        For now we'll confirm and continue.
        <br />
        <br />
        Tap <b>Confirm</b> to continue.
      </>
    ),
    target: ".tutorial-confirm",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },
  {
    id: 41,
    title: "Video Guide",
    text: (
      <>
        Don’t worry if you don’t remember every step.
        <br />
        <br />
        Throughout the tool, you’ll see short video tutorials showing a screen
        recording of someone completing the exact step you’re on.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Don’t worry if you don’t remember every step.
        <br />
        <br />
        Throughout the tool, you’ll see short video tutorials showing a screen
        recording of someone completing the exact step you’re on.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".instruction-container",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 42,
    title: "Video Guide",
    text: (
      <>
        You can click this button at any time to watch the video.
        <br />
        <br />
        For now, let’s keep walking through the tool.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can tap this button at any time to watch the video.
        <br />
        <br />
        For now, let’s keep walking through the tool.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".instruction-video-button",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 43,
    title: "Draw Polygon Button",
    text: (
      <>
        This button lets you restart the drawing process if it stops or gets
        interrupted for any reason.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This button lets you restart the drawing process if it stops or gets
        interrupted for any reason.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".mapbox-gl-draw_polygon",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 44,
    title: "Delete Polygon Button",
    text: (
      <>
        You can use this button to delete a polygon if you want to redraw or
        remove it from the map.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can use this button to delete a polygon if you want to redraw or
        remove it from the map.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".mapbox-gl-draw_trash",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 45,
    title: "Combine Polygons Button",
    text: (
      <>
        If the field you’d like to investigate has multiple separate sections,
        you can use this button to group them together as a single field for
        processing.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        If the field you’d like to investigate has multiple separate sections,
        you can use this button to group them together as a single field for
        processing.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".mapbox-gl-draw_combine",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 46,
    title: "Un-Combine Polygons Button",
    text: (
      <>
        If you combined too many polygons into a single field, you can use this
        button to separate them into individual polygons and delete or redraw
        any incorrect ones if needed.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        If you combined too many polygons into a single field, you can use this
        button to separate them into individual polygons and delete or redraw
        any incorrect ones if needed.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".mapbox-gl-draw_uncombine",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 47,
    title: "Zoom In",
    text: (
      <>
        The drawing process works best when you're zoomed in so your field fills
        most of the screen.
        <br />
        <br />
        <ul style={{ paddingLeft: "1.2em" }}>
          <li>
            <b>Scroll up</b> to zoom in
          </li>
          <li>
            <b>Scroll down</b> to zoom out
          </li>
          <li>
            <b>Click and drag</b> to move or recenter the map
          </li>
        </ul>
        Once your field is clearly in view, click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        The drawing process works best when you're zoomed in so your field fills
        most of the screen.
        <br />
        <br />
        <ul style={{ paddingLeft: "1.2em" }}>
          <li>
            <b>Pinch out</b> to zoom in
          </li>
          <li>
            <b>Pinch in</b> to zoom out
          </li>
          <li>
            <b>Tap and drag</b> to move or recenter the map
          </li>
        </ul>
        Once your field is clearly in view, tap <b>Next</b> to continue.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 48,
    title: "Adding Vertices",
    text: <>Click on the map to add your first vertex</>,
    tabletText: <>Tap on the map to add your first vertex</>,
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 49,
    title: "Drawing a Line",
    text: (
      <>
        Now as you move your mouse around you'll see a dotted line.
        <br />
        <br />
        That dotted line is the edge of your polygon and should correllate
        closely with the boundary of the field or portion of field you'd like to
        learn more about.
        <br />
        <br />
        <b>Click the map</b> again to set the line, place another vertex, and
        continue.
      </>
    ),
    tabletText: (
      <>
        Now that you've placed your first vertex, the next tap on the map will
        draw a line.
        <br />
        <br />
        The dotted line is the edge of your polygon and should correllate
        closely with the boundary of the field or portion of field you'd like to
        learn more about.
        <br />
        <br />
        <b>Tap the map</b> to create a line, place another vertex, and continue.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 50,
    title: "Making Another Line",
    text: (
      <>
        You should now see a triangle that moves with your mouse. You can make
        your boundary as precise as you'd like by adding more verticies to make
        smaller sections of straight lines.
        <br />
        <br />
        For now we're just going to make a basic rectangle.
        <br />
        <br />
        <b>Click the map</b> again to set the second line of your polygon, place
        another vertex and continue.
      </>
    ),
    tabletText: (
      <>
        Now you should see a straight dotted line between the two points you've
        placed.
        <br />
        <br />
        When you tap again, you'll place a third vertex and you should see a
        triangle formed. This isn't the final polygon, but shows the area you
        have drawn so far. We will just be drawing a simple rectangle for now.
        <br />
        <br />
        <b>Tap the map</b> again to set place the third vertex, draw the second
        edge of your field boundary and continue.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 51,
    title: "Finalizing the Boundary",
    text: (
      <>
        You should now see a triangle with three straight sides that follows
        your mouse.
        <br />
        <br />
        Click to place your <b>fourth vertex</b>. This will create a four-sided
        polygon.
        <br />
        <br />
        But notice — your mouse is still trying to draw more lines.
        <br />
        <br />
        To finish your polygon, click either the <b>first</b> vertex you placed
        or the <b>last</b> one.
        <br />
        <br />
        This will finalize your field boundary.
      </>
    ),
    tabletText: (
      <>
        You should now see a triangle with three straight sides.
        <br />
        <br />
        Tap to place your <b>fourth vertex</b>. This will create a four-sided
        polygon.
        <br />
        <br />
        But the shape is not complete!
        <br />
        <br />
        To finalize your polygon, tap either the <b>first</b> vertex you placed
        or the <b>last</b> one again.
        <br />
        <br />
        This will finalize your field boundary polygon.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 52,
    title: "Finished Polygon",
    text: (
      <>
        Nice work!
        <br />
        <br />
        You should now see a completed 4-sided polygon with an acreage
        measurment, and your mouse is no longer drawing extra lines.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Nice work!
        <br />
        <br />
        You should now see a completed 4-sided polygon, with an acreage
        measurment in the middle.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 53,
    title: "Adjustments",
    text: (
      <>
        You should see 4 straight lines, and an acreage label in the center.
        <br />
        <br />
        You can always <b>adjust the shape</b> later by clicking and dragging
        any of the white points (vertices).
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You should see <b>4 vertices</b>, 4 straight lines, and an acreage label
        in the center.
        <br />
        <br />
        When not in the tutorial, you can <b>adjust the shape</b> by tapping and
        dragging any of the white points (vertices).
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".map-wrapper",
    route: "/Map",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 54,
    title: "Submit",
    text: (
      <>
        Now that you've drawn a polygon to represent the field or portion of a
        field you're interested in:
        <br />
        <br />
        Click <b>Submit</b> to generate a detailed field report.
      </>
    ),
    tabletText: (
      <>
        Now that you've drawn a polygon to represent the field or portion of a
        field you're interested in:
        <br />
        <br />
        Tap <b>Submit</b> to generate a detailed field report.
      </>
    ),
    target: ".submit-button",
    route: "/Map",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 55,
    title: "Your Field Report",
    text: (
      <>
        This section shows the basic information about your field and the cover
        types and priorities you selected earlier.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This section shows the basic information about your field and the cover
        types and priorities you selected earlier.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-basic-cont",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 56,
    title: "Eligible Practices",
    text: (
      <>
        Based on your field boundary, we've identified practices you're most
        likely eligible for.
        <br /> <br />
        Here you'll see cards similar to those from Explore Practices, but
        they're a bit more compact.
        <br />
        <br />
        They've been sorted based on your priorities, and show basic information
        about the practices. You can click on each practice to open a dropdown
        with a detailed breakdown of your estimated payment.
        <br />
        <br />
        <b>Click the practice card</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Based on your field boundary, we've identified practices you're most
        likely eligible for.
        <br /> <br />
        Here you'll see cards similar to those from Explore Practices, but
        they're a bit more compact.
        <br />
        <br />
        They've been sorted based on your priorities, and show basic information
        about the practices. You can tap on each practice to open a dropdown
        with a detailed breakdown of your estimated payment.
        <br />
        <br />
        <b>Tap the practice card</b> to continue.
      </>
    ),
    target: ".practice-card-container",
    route: "/FieldReport",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 57,
    title: "Estimated Payments",
    text: (
      <>
        Here you'll find the 3 soil types with the most acreage on your
        indicated field. And their associated rental rate.
        <br />
        <br />
        You can see any additional incentives that may apply, how often you'd
        recieve the benefit, and how much the benefit could be.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Here you'll find the 3 soil types with the most acreage on your
        indicated field. And their associated rental rate.
        <br />
        <br />
        You can see any additional incentives that may apply, how often you'd
        recieve the benefit, and how much the benefit could be.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-practice-detail",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 58,
    title: "Practice Details",
    text: (
      <>
        You can click this link to open a new tab that goes into greater detail
        on this pracitce.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can tap this link to open a new tab that goes into greater detail on
        this pracitce.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".detail-button",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 59,
    title: "Map",
    text: (
      <>
        This shows your drawn polygon representing the field you'd like to
        enroll or explore.
        <br />
        <br />
        You may see multiple shapes within your boundary. These represent the
        different soil types detected.
        <br />
        <br />
        Hovering over each soil shape will highlight it.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This shows your drawn polygon representing the field you'd like to
        enroll or explore.
        <br />
        <br />
        You may see multiple shapes within your boundary. These represent the
        different soil types detected.
        <br />
        <br />
        Tap each soil shape to highlight it.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-map-inner-container",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 60,
    title: "Layers",
    text: (
      <>
        This button is similar to the one in the previous map view — but with
        different options.
        <br />
        <br />
        <b>Click the layers icon</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This button is similar to the one in the previous map view — but with
        different options.
        <br />
        <br />
        <b>Tap the layers icon</b> to continue.
      </>
    ),
    target: ".report-layer-select",
    route: "/FieldReport",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 61,
    title: "Layer Options",
    text: (
      <>
        You can still switch between satellite and street views using the base
        layer toggle.
        <br />
        <br />
        Below that, you’ll see optional layers to help analyze your field:
        <br />
        <ul style={{ fontSize: "14px" }}>
          <li style={{ lineHeight: "18px" }}>
            <b>Soil Types</b> – Shows soil type locations within your field.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>SAFE Boundaries</b> – Shows if the field is within a USDA SAFE
            zone.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>Floodplain</b> – Highlights areas within a 100-year floodplain.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>Water Features</b> – Streams, ponds, and other surface water.
          </li>
        </ul>
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can still switch between satellite and street views using the base
        layer toggle.
        <br />
        <br />
        Below that, you’ll see optional layers to help analyze your field:
        <br />
        <ul style={{ fontSize: "14px" }}>
          <li style={{ lineHeight: "18px" }}>
            <b>Soil Types</b> – Shows soil type locations within your field.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>SAFE Boundaries</b> – Shows if the field is within a USDA SAFE
            zone.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>Floodplain</b> – Highlights areas within a 100-year floodplain.
          </li>
          <li style={{ lineHeight: "18px" }}>
            <b>Water Features</b> – Streams, ponds, and other surface water.
          </li>
        </ul>
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-layer-options",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 62,
    title: "Soil Information",
    text: (
      <>
        This table shows the soil types found on your field, along with their
        acreage and rental rates.
        <br />
        <br />
        The addtional tabs are for specific practices that utilize{" "}
        <b>Grassland</b> and/or <b>Pastureland</b> rental rates.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This table shows the soil types found on your field, along with their
        acreage and rental rates.
        <br />
        <br />
        The addtional tabs are for specific practices that utilize{" "}
        <b>Grassland</b> and/or <b>Pastureland</b> rental rates.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-rental-table-container",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  // 63 (older "Additional Rental Rates") intentionally omitted

  {
    id: 63,
    title: "CREP Eligible Practices",
    text: (
      <>
        Like in Explore Practices, some practices may be flagged as eligible for
        CREP. These provide additional incentives and different payment
        estimates.
        <br />
        <br />
        Click <b>the Practice Card</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Like in Explore Practices, some practices may be flagged as eligible for
        CREP. These provide additional incentives and different payment
        estimates.
        <br />
        <br />
        Tap <b>the Practice Card</b> to continue.
      </>
    ),
    target: ".tutorial-crep-card",
    route: "/FieldReport",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 64,
    title: "CREP Tabs",
    text: (
      <>
        These tabs let you switch between payment estimates for different
        program types like General Signup or specific CREPs.
        <br />
        <br />
        Click a <b>CREP tab</b> to continue.
      </>
    ),
    tabletText: (
      <>
        These tabs let you switch between payment estimates for different
        program types like General Signup or specific CREPs.
        <br />
        <br />
        Tap a <b>CREP tab</b> to continue.
      </>
    ),
    target: ".report-crep-options",
    route: "/FieldReport",
    advanceType: "custom",
    canScroll: false,
  },

  {
    id: 65,
    title: "CREP Payment Estimates",
    text: (
      <>
        This section updates with additional incentives the selected CREP may
        include.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        This section updates with additional incentives the selected CREP may
        include.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".crep-payment",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 66,
    title: "Your Local USDA Office",
    text: (
      <>
        Here you'll find the location and contact information for a local USDA
        FSA office
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        Here you'll find the location and contact information for a local USDA
        FSA office
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".local-usda-container",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 67,
    title: "Update Filters",
    text: (
      <>
        If you'd like to adjust your previous selections, you can use the{" "}
        <i>Update Filters</i> button at any time.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        If you'd like to adjust your previous selections, you can use the{" "}
        <i>Update Filters</i> button at any time.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".filter-button",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 68,
    title: "Log In",
    text: (
      <>
        You can create a profile or log in to save your field and selections for
        future reference.
        <br />
        <br />
        This allows you to return later without having to re-enter each of your
        selections.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can create a profile or log in to save your field and selections for
        future reference.
        <br />
        <br />
        This allows you to return later without having to re-enter each of your
        selections.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".log-in",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 69,
    title: "Print Report",
    text: (
      <>
        You can use this button to print a summary report with your field
        boundary and all potentially eligible practices, including estimated
        payments.
        <br />
        <br />
        It’s helpful to bring to your local office or keep for your records.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can use this button to print a summary report with your field
        boundary and all potentially eligible practices, including estimated
        payments.
        <br />
        <br />
        It’s helpful to bring to your local office or keep for your records.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-button.print",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 70,
    title: "Download Shapefile",
    text: (
      <>
        You can use this button to download a shapefile (.zip) of your drawn
        field boundary.
        <br />
        <br />
        These files can be imported into GIS software or shared with USDA staff
        to help streamline enrollment.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    tabletText: (
      <>
        You can use this button to download a shapefile (.zip) of your drawn
        field boundary.
        <br />
        <br />
        These files can be imported into GIS software or shared with USDA staff
        to help streamline enrollment.
        <br />
        <br />
        Tap <b>Next</b> to continue.
      </>
    ),
    target: ".report-button.shapefile",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 71,
    title: "You're All Set!",
    text: (
      <>
        You've completed the full tutorial — great job!
        <br />
        <br />
        You now know how to draw your field, filter practices, review payment
        estimates, and use tools like printing, saving, and downloading
        shapefiles.
        <br />
        <br />
        Feel free to explore the map, revisit your selections, or reach out to
        your local USDA office using the info provided.
        <br />
        <br />
        Thanks for taking the time to learn how to get the most out of this
        tool!
      </>
    ),
    target: "center",
    route: "/FieldReport",
    advanceType: "next",
    canScroll: false,
  },
];

export default tutorialSteps;
